<template>
  <div class="widget__content-center widget__content-center--flex">
    <div class="widget__header-text">
      Showing daily repeat <span>{{ intraday === true ? 'intraday' : 'interday' }}</span> visitors
    </div>

    <div v-if="chartSeries.length" class="chart-wrapper">
      <apexchart
        type="bar"
        height="100%"
        width="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>

    <div class="widget__header-controls widget__above-footer">
      <li
        @click="setIntraday(true)"
        class="widget__header-control"
        :class="{ 'widget__header-control--active': intraday === true }"
      >
        Intraday
      </li>
      <li
        @click="setIntraday(false)"
        class="widget__header-control"
        :class="{ 'widget__header-control--active': intraday === false }"
      >
        Interday
      </li>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
dayjs.extend(advancedFormat);

import { barColors } from '@/utils/chartColors';

import type { WidgetFilter, WidgetSetting, WidgetTypeRepeatVisitors } from '@/types/Widget';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeRepeatVisitors;
  area?: WidgetFilter;
  cluster?: WidgetSetting;
}>();

const intraday = ref(true);

const chartData = computed(() => {
  let baseData;
  const intraval = intraday.value ? 'Intraday' : 'Interday';

  if (props.area?.name === 'Site') {
    baseData = props.widget.data['Site'][intraval]['Day'];
  } else if (props.area?.name === 'Cluster' && props.cluster) {
    baseData = props.widget.data['Cluster'][props.cluster.id][intraval]['Day'];
  }

  if (baseData) {
    let dataToFormat: any = baseData;
    if (intraday.value) {
      const transformedData: { [key: string]: { Engaged: number; Passerby: number } } = {};
      Object.keys(baseData).forEach((key) => {
        transformedData[key] = {
          Engaged: baseData[key].Engaged,
          Passerby: baseData[key].OTS - baseData[key].Engaged,
        };
      });
      dataToFormat = transformedData;
    }

    const formatted = Object.keys(dataToFormat).map((key) => {
      const date = dayjs(key, 'YYYYMMDD').format('YYYY-MM-DD');

      return {
        name: date,
        data: dataToFormat[key],
      };
    });

    return formatted;
  }

  return null;
});

const chartSeries = computed(() => {
  if (chartData.value) {
    type barData = {
      x: string;
      y: number;
    };

    const dataPointOne: barData[] = [];
    const dataPointTwo: barData[] = [];

    chartData.value.forEach((char) => {
      Object.keys(char.data).forEach((key, index) => {
        if (index === 0) {
          dataPointOne.push({
            x: key,
            y: char.data[key],
          });
        } else {
          dataPointTwo.push({
            x: key,
            y: char.data[key],
          });
        }
      });
    });

    return [
      {
        name: dataPointOne[0].x,
        data: dataPointOne,
      },
      {
        name: dataPointTwo[0].x,
        data: dataPointTwo,
      },
    ];
  }
  return [];
});

const chartLabels = computed(() => {
  if (chartData.value) {
    return chartData.value.map((point) => point.name);
  }
  return [];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => ({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    id: 'meshh-chart-repeat-visitors',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      'Day',
      '',
      props.cluster?.title || '',
      '',
    ),
    type: 'bar',
    stacked: true,
  },
  colors: barColors,
  xaxis: {
    categories: chartLabels.value,
    labels: {
      formatter(value: string) {
        return dayjs(value).format('Do MMM');
      },
    },
  },
  tooltip: {
    x: {
      formatter: (val, opts) => {
        const data = chartData.value;
        const index = opts?.dataPointIndex ?? 0;

        if (data) {
          return dayjs(data[index].name).format('Do MMM');
        }

        return '';
      },
    },
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0);
      },
    },
  },
  grid: {
    show: false,
  },
  dataLabels: {
    enabled: false,
  },
}));

const setIntraday = (state: boolean) => {
  intraday.value = state;
};
</script>

<style lang="scss">
.widget__header-controls.widget__above-footer {
  @include mixins.flex-center($horiz: center);
  width: 100%;
}
</style>
